import { useEffect, useRef, useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getSpinnerAngleDeg, getFirstSpin, reportShapeMiss } from './spinnerMfeBridge';

/*
  Click-to-mark + frame/zoom capture for the spinner damage step. Ported from the
  spinner-standalone POC's useDamagePins.ts with ALL 3D removed (no panel3D /
  meshRaycast / spinnerProjection). The geometry-derived Driver/Passenger side
  (resolveSideFromGeometry) is pure trig on the turntable angle — kept, since it
  improves the LLM's side call without any 3D model. Panel identification is the
  server-side: the upload-damage-images endpoint runs the LLM panel evaluation.

  All access to the third-party spinner MFE's internals (angle, spin node) lives in
  spinnerMfeBridge — this hook depends on those typed accessors, not on window globals.
*/

export interface CanvasRect { left: number; top: number; width: number; height: number; }

export interface DamagePin {
  id: string;
  x: number;
  y: number;
  // Pin position as a fraction (0–1) of the car image canvas — resize-stable anchor.
  canvasFraction: { x: number; y: number };
  stockNumber: number;
  timestamp: number;
  thumbnail: string;
  zoomThumbnail: string;
  locationData: {
    positionPct: { x: number; y: number };
    containerSize: { w: number; h: number };
    frameIndex: number | null;
    viewDescription: string;
    angleDeg: number | null;
    frameImageUrl: string;
    panelName: string;
    panelId: string;
    resolvedSide: 'Driver' | 'Passenger' | null;
    confidence: string;
    reasoning: string;
    panelLoading: boolean;
    aiTimeMs: number | null;
  };
}

// ── DOM Reading ───────────────────────────────────────────
const CONTAINER_SELECTOR = '[data-qa="ExteriorHotspotContainer"]';
// The MFE's hotspot-projection overflow element. markAtPointer (SpinnerLocation) and addPin
// convert click coordinates against the SAME element, so the selector is shared — if the two
// ever queried different elements, every pin would land offset by the gap between the boxes.
export const HOTSPOT_OVERFLOW_SELECTOR = `${CONTAINER_SELECTOR} [style*="overflow"]`;

function readContainerSize(root: Element): { w: number; h: number } | null {
  const overflowEl = root.querySelector<HTMLElement>(HOTSPOT_OVERFLOW_SELECTOR);
  if (!overflowEl) return null;
  const w = overflowEl.offsetWidth;
  const h = overflowEl.offsetHeight;
  return w && h ? { w, h } : null;
}

// Downscale-sample a canvas to gauge how much real car content it holds: opaque coverage
// AND colour variety (a flat fill has neither). Returns a positive score for content, 0
// for empty/uniform, and -1 when it can't be sampled (WebGL/tainted — drawImage yields
// nothing useful). Cheap: a 24×24 draw + read.
function canvasContentScore(c: HTMLCanvasElement): number {
  try {
    const N = 24;
    const s = document.createElement('canvas');
    s.width = N;
    s.height = N;
    const x = s.getContext('2d');
    if (!x) return -1;
    x.drawImage(c, 0, 0, N, N);
    const d = x.getImageData(0, 0, N, N).data;
    let opaque = 0;
    const colors = new Set<string>();
    for (let i = 0; i < d.length; i += 4) {
      if (d[i + 3] > 16) opaque += 1;
      colors.add(`${d[i] >> 4},${d[i + 1] >> 4},${d[i + 2] >> 4}`);
    }
    // Require both meaningful opaque coverage and colour variety so a transparent overlay
    // or a solid-fill surface never reads as "the car".
    return opaque >= N * N * 0.2 && colors.size >= 8 ? opaque * colors.size : 0;
  } catch {
    return -1;
  }
}

// Pick the car-render canvas among the spinner's canvases. Once the spinner enters its
// zoom/immersive state it adds a second, viewport-sized canvas that is mostly TRANSPARENT.
// Size-based selection is unreliable: that overlay has a bigger CSS box than the car, and
// on high-DPR phones its BUFFER can be bigger too — either way, picking it produced a
// car-less capture and a false "not on vehicle" from the model. Instead select by actual
// CONTENT (opaque, varied pixels = the car), which is DPR-independent. Fall back to the
// largest buffer only if nothing can be sampled (all WebGL/tainted).
function findCarImageCanvas(root: Element): HTMLCanvasElement | null {
  const canvases = Array.from(root.querySelectorAll('canvas')).filter(
    (c) => (c as HTMLCanvasElement).width > 0 && (c as HTMLCanvasElement).height > 0
  ) as HTMLCanvasElement[];
  if (!canvases.length) return null;

  let best: HTMLCanvasElement | null = null;
  let bestContent = 0;
  canvases.forEach((c) => {
    const content = canvasContentScore(c);
    if (content > bestContent) {
      bestContent = content;
      best = c;
    }
  });
  if (best) return best;

  // Fallback: nothing sampled as "has content" — pick the largest buffer.
  let bestArea = 0;
  canvases.forEach((c) => {
    const area = c.width * c.height;
    if (area > bestArea) {
      bestArea = area;
      best = c;
    }
  });
  return best;
}

// Canvas rect in the ZOOM LAYER's local (untransformed) coordinate space. The pin overlay
// renders inside the transformed layer, so its coordinates must be pre-transform: measure
// both rects in the same frame and divide out the layer's live scale. Because the canvas
// doesn't move within the layer, this rect is CONSTANT during the camera animation — the
// dot stays mathematically glued to the car instead of chasing it a frame behind.
function readCanvasRectRelative(root: Element): CanvasRect | null {
  const canvas = findCarImageCanvas(root);
  if (!canvas) return null;
  const cr = canvas.getBoundingClientRect();
  if (!cr.width || !cr.height) return null;
  const layer = root.querySelector<HTMLElement>('[data-zoom-layer]');
  if (layer && layer.offsetWidth) {
    const lr = layer.getBoundingClientRect();
    const scale = lr.width / layer.offsetWidth;
    if (scale > 0) {
      return {
        left: (cr.left - lr.left) / scale,
        top: (cr.top - lr.top) / scale,
        width: cr.width / scale,
        height: cr.height / scale,
      };
    }
  }
  const pr = root.getBoundingClientRect();
  return { left: cr.left - pr.left, top: cr.top - pr.top, width: cr.width, height: cr.height };
}

// ── Spinner rotation ground truth ─────────────────────────
// 0° = front, 90° = driver/left, 180° = rear, 270° = passenger/right.
function describeView(angle: number): string {
  if (angle < 22.5 || angle >= 337.5) return 'front view';
  if (angle < 67.5) return 'front-left view';
  if (angle < 112.5) return 'left side view';
  if (angle < 157.5) return 'rear-left view';
  if (angle < 202.5) return 'rear view';
  if (angle < 247.5) return 'rear-right view';
  if (angle < 292.5) return 'right side view';
  return 'front-right view';
}

// Spins expose a frame count; fall back to a typical turntable size when absent.
const DEFAULT_FRAME_COUNT = 64;

function estimateFrame(): { frameIndex: number | null; viewDescription: string; angleDeg: number | null } {
  try {
    const spin = getFirstSpin();
    const angle = getSpinnerAngleDeg();
    if (angle == null) return { frameIndex: null, viewDescription: 'unknown', angleDeg: null };
    const viewDescription = describeView(angle);
    const frameCount = spin?.cameras?.frameCount || spin?.images?.urls?.length || DEFAULT_FRAME_COUNT;
    const bestFrame = Math.round(angle / (360 / frameCount)) % frameCount;
    return { frameIndex: bestFrame, viewDescription, angleDeg: angle };
  } catch {
    return { frameIndex: null, viewDescription: 'unknown', angleDeg: null };
  }
}

// ── Thumbnail Capture ─────────────────────────────────────
function drawViewLabel(ctx: CanvasRenderingContext2D, w: number, viewLabel: string) {
  ctx.save();
  ctx.font = 'bold 36px Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.strokeStyle = '#000';
  ctx.lineWidth = 6;
  ctx.strokeText(viewLabel, w / 2, 40);
  ctx.fillStyle = '#fff';
  ctx.fillText(viewLabel, w / 2, 40);

  const sideLabel = viewLabel.includes('LEFT')
    ? '( DRIVER SIDE - LEFT )'
    : viewLabel.includes('RIGHT')
    ? '( PASSENGER SIDE - RIGHT )'
    : null;
  if (sideLabel) {
    ctx.font = 'bold 28px Arial, sans-serif';
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 5;
    ctx.strokeText(sideLabel, w / 2, 72);
    ctx.fillStyle = '#ffff00';
    ctx.fillText(sideLabel, w / 2, 72);
  }
  ctx.restore();
}

function drawCrosshair(ctx: CanvasRenderingContext2D, cx: number, cy: number) {
  const armLen = 60;
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.moveTo(cx - armLen, cy);
  ctx.lineTo(cx + armLen, cy);
  ctx.moveTo(cx, cy - armLen);
  ctx.lineTo(cx, cy + armLen);
  ctx.stroke();

  ctx.strokeStyle = '#ff0000';
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(cx - armLen, cy);
  ctx.lineTo(cx + armLen, cy);
  ctx.moveTo(cx, cy - armLen);
  ctx.lineTo(cx, cy + armLen);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(cx, cy, 24, 0, Math.PI * 2);
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 5;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(cx, cy, 24, 0, Math.PI * 2);
  ctx.strokeStyle = '#ff0000';
  ctx.lineWidth = 3;
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(cx, cy, 6, 0, Math.PI * 2);
  ctx.fillStyle = '#ff0000';
  ctx.fill();
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 2;
  ctx.stroke();
}

// OpenAI high-detail vision rescales to ~768px short side; 1152 wide ≈ that target.
const FULL_W = 1152;
const ZOOM_W = 768;
const JPEG_Q = 0.82;
// Zoom-crop window as a fraction of the full capture, centered on the crosshair.
const ZOOM_CROP_RATIO = 0.3;
// Wait out the confirm step's camera move (700ms) before the expensive capture.
const CAPTURE_DELAY_MS = 850;
// Give a CDN reference-frame fallback image this long to load before giving up on it.
const IMAGE_LOAD_TIMEOUT_MS = 5000;

async function captureSpinnerThumbnail(
  root: Element,
  // The pin as a fraction (0–1) of the car image canvas. Scale-invariant, so the
  // crosshair stays correct even if the zoom layer is mid-animation (the confirm step's
  // camera move starts the moment the pin lands, before this deferred capture runs).
  canvasFraction: { x: number; y: number }
): Promise<{ full: string; zoom: string; cxNorm: number | null }> {
  const empty = { full: '', zoom: '', cxNorm: null };
  try {
    const spin = getFirstSpin();
    if (!spin?.images?.urls) return empty;

    const frameInfo = estimateFrame();
    const frameIdx = frameInfo.frameIndex ?? 0;
    const imageUrl = spin.images.urls[frameIdx];
    if (!imageUrl) return empty;

    const imgCanvas = findCarImageCanvas(root);
    // Guard the raw buffer dimensions: a canvas found before it's sized would make thumbH
    // NaN and produce an unusable/garbage data URL.
    if (!imgCanvas || !imgCanvas.width || !imgCanvas.height) return empty;

    const thumbW = FULL_W;
    const thumbH = Math.round(thumbW * (imgCanvas.height / imgCanvas.width));

    const offscreen = document.createElement('canvas');
    offscreen.width = thumbW;
    offscreen.height = thumbH;
    const ctx = offscreen.getContext('2d');
    if (!ctx) return empty;

    ctx.drawImage(imgCanvas, 0, 0, thumbW, thumbH);

    const testPixel = ctx.getImageData(thumbW / 2, thumbH / 2, 1, 1).data;
    const isBlank = testPixel[0] === 0 && testPixel[1] === 0 && testPixel[2] === 0 && testPixel[3] === 0;

    if (isBlank && imageUrl) {
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject();
          img.src = imageUrl;
          setTimeout(() => reject(), IMAGE_LOAD_TIMEOUT_MS);
        });
        ctx.drawImage(img, 0, 0, thumbW, thumbH);
      } catch {
        // Capture was blank AND the CDN reference-frame fallback failed (CORS/timeout).
        // Do NOT send a car-less image to the model — that yields a false "not on
        // vehicle". Bail with empty so the server skips the eval (location stays 'Other').
        return empty;
      }
    }

    const cx = canvasFraction.x * thumbW;
    const cy = canvasFraction.y * thumbH;

    const viewLabel = frameInfo.viewDescription.toUpperCase().replace(' VIEW', '');

    let zoom = '';
    const cropW = Math.round(thumbW * ZOOM_CROP_RATIO);
    const cropH = Math.round(thumbH * ZOOM_CROP_RATIO);
    const cropX = Math.max(0, Math.min(thumbW - cropW, Math.round(cx - cropW / 2)));
    const cropY = Math.max(0, Math.min(thumbH - cropH, Math.round(cy - cropH / 2)));
    const zoomCanvas = document.createElement('canvas');
    zoomCanvas.width = ZOOM_W;
    zoomCanvas.height = Math.round(ZOOM_W * (cropH / cropW));
    const zctx = zoomCanvas.getContext('2d');
    if (zctx) {
      zctx.drawImage(offscreen, cropX, cropY, cropW, cropH, 0, 0, zoomCanvas.width, zoomCanvas.height);
      drawViewLabel(zctx, zoomCanvas.width, `${viewLabel} (ZOOMED)`);
      drawCrosshair(zctx, ((cx - cropX) / cropW) * zoomCanvas.width, ((cy - cropY) / cropH) * zoomCanvas.height);
      zoom = zoomCanvas.toDataURL('image/jpeg', JPEG_Q);
    }

    drawViewLabel(ctx, thumbW, viewLabel);
    drawCrosshair(ctx, cx, cy);

    return { full: offscreen.toDataURL('image/jpeg', JPEG_Q), zoom, cxNorm: cx / thumbW };
  } catch {
    return empty;
  }
}

// Resolve Driver/Passenger from the turntable angle + crosshair x (definitive only
// at front/rear, where the image-left/right ↔ side mapping flips). American LHD.
// A crosshair within this fraction of center is too ambiguous to call a side.
const SIDE_DEADZONE = 0.06;

export function resolveSideFromGeometry(
  angleDeg: number | null,
  cxNorm: number | null
): 'Driver' | 'Passenger' | null {
  if (angleDeg == null || cxNorm == null) return null;
  const off = cxNorm - 0.5;
  if (Math.abs(off) < SIDE_DEADZONE) return null;
  const a = ((angleDeg % 360) + 360) % 360;
  const isFront = a < 22.5 || a >= 337.5;
  const isRear = a >= 157.5 && a < 202.5;
  if (isFront) return off < 0 ? 'Passenger' : 'Driver';
  if (isRear) return off < 0 ? 'Driver' : 'Passenger';
  return null;
}

// A generated id for a pin — crypto.randomUUID isn't available outside secure contexts /
// on older WebViews, so fall back to the uuid lib there.
function generatePinId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return uuidv4();
}

// ── Hook ──────────────────────────────────────────────────
export function useDamagePins(containerRef: React.RefObject<HTMLElement | null>) {
  const [pins, setPins] = useState<DamagePin[]>([]);
  const [canvasRect, setCanvasRect] = useState<CanvasRect | null>(null);
  const lastCanvasKeyRef = useRef('');

  // Latest pins snapshot for imperative reads (captureNow) that must not close over
  // a stale render's state.
  const pinsRef = useRef<DamagePin[]>([]);
  pinsRef.current = pins;

  // Guards setPins from firing after the hook has unmounted (a deferred capture can resolve
  // after Confirm navigates away).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Deferred-capture timers, so they can be cancelled on unmount / re-mark instead of firing
  // a superfluous capture for a pin that no longer exists.
  const captureTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  // In-flight/completed capture promise per pin id. Both the deferred timer (addPin)
  // and an on-demand capture (captureNow, e.g. Confirm before the timer fired) share
  // the SAME promise, so the frame is captured at most once per pin.
  const captureRef = useRef<Map<string, Promise<{ full: string; zoom: string }>>>(new Map());

  // Start (or reuse) the frame + zoom-crop capture for a pin, patch the pin state when
  // it resolves, and return just the two base64 images.
  const runCapture = useCallback(
    (pinId: string, canvasFraction: { x: number; y: number }): Promise<{ full: string; zoom: string }> => {
      const existing = captureRef.current.get(pinId);
      if (existing) return existing;
      const el = containerRef.current;
      if (!el) return Promise.resolve({ full: '', zoom: '' });
      const task = captureSpinnerThumbnail(el, canvasFraction).then((captured) => {
        if (mountedRef.current) {
          setPins((prev) =>
            prev.map((p) => (p.id === pinId ? { ...p, thumbnail: captured.full, zoomThumbnail: captured.zoom } : p))
          );
        }
        return { full: captured.full, zoom: captured.zoom };
      });
      captureRef.current.set(pinId, task);
      return task;
    },
    [containerRef]
  );

  // Guarantee a pin's images before Confirm uses them: return the already-captured
  // images if present, otherwise run the capture now and await it. Fixes the race where
  // Confirm fired before the CAPTURE_DELAY_MS deferred capture had produced the images,
  // sending empty base64 to upload-damage-images (server then skips DocMan + the LLM).
  const captureNow = useCallback(
    async (pinId: string): Promise<{ full: string; zoom: string }> => {
      const latest = pinsRef.current.find((p) => p.id === pinId);
      if (latest?.thumbnail) return { full: latest.thumbnail, zoom: latest.zoomThumbnail };
      if (!latest) return { full: '', zoom: '' };
      return runCapture(pinId, latest.canvasFraction);
    },
    [runCapture]
  );

  // Keep canvasRect fresh WITHOUT a perpetual 60fps layout-read loop. The MFE renders and
  // sizes its canvas asynchronously, so measure on a bounded startup poll until the rect
  // appears and holds steady, then stop and let a ResizeObserver catch later layout changes.
  // (canvasRect is in the zoom layer's local space, so it's transform-invariant — it does
  // NOT change during the camera animation, which is why we don't need to keep polling.)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    const measure = (): boolean => {
      const cr = readCanvasRectRelative(el);
      if (!cr) return false;
      const ckey = `${Math.round(cr.left)},${Math.round(cr.top)},${Math.round(cr.width)},${Math.round(cr.height)}`;
      if (ckey !== lastCanvasKeyRef.current) {
        lastCanvasKeyRef.current = ckey;
        setCanvasRect(cr);
      }
      return true;
    };

    const POLL_MS = 100;
    const MAX_ATTEMPTS = 160; // ~16s ceiling, matched to the frames-ready timeout
    const STABLE_READS_NEEDED = 3;
    let attempts = 0;
    let stableReads = 0;
    let pollId: ReturnType<typeof setTimeout> | undefined;
    const poll = () => {
      const prevKey = lastCanvasKeyRef.current;
      const hasCanvas = measure();
      attempts += 1;
      stableReads = hasCanvas && lastCanvasKeyRef.current === prevKey ? stableReads + 1 : 0;
      if (hasCanvas && stableReads >= STABLE_READS_NEEDED) return; // settled
      if (attempts >= MAX_ATTEMPTS) return;
      pollId = setTimeout(poll, POLL_MS);
    };
    // Give the MFE a moment to mount before the first read (matches the previous delay).
    const startId = setTimeout(poll, 500);

    const ro = new ResizeObserver(() => measure());
    ro.observe(el);

    return () => {
      clearTimeout(startId);
      if (pollId) clearTimeout(pollId);
      ro.disconnect();
    };
  }, [containerRef]);

  const addPin = useCallback(
    (clickX: number, clickY: number, stockNumber: number): string | undefined => {
      const el = containerRef.current;
      if (!el) return undefined;

      // SERV-583: the hotspot overflow element is the MFE's 3D-projection surface and only
      // mounts when the vehicle's camera model (cameras.gltf) loads — vehicles with
      // is3DModelValid=false never get one. It is only a coordinate frame + metadata source
      // for the LLM prompt, NEVER a requirement for placing the pin (the pin's real anchor is
      // canvasFraction against the car canvas, which exists for those vehicles too). Treating
      // it as a hard gate silently bricked tap-to-mark on every device for those vehicles, so
      // fall back to the pane's own box instead. markAtPointer applies the same overflow-else-
      // pane fallback when converting the click, so the coordinate base always matches.
      const overflowEl = el.querySelector<HTMLElement>(HOTSPOT_OVERFLOW_SELECTOR);
      if (!overflowEl) reportShapeMiss('hotspot-overflow-missing');
      const paneRect = el.getBoundingClientRect();
      const size = readContainerSize(el) ?? { w: Math.round(paneRect.width), h: Math.round(paneRect.height) };
      if (!size.w || !size.h) return undefined;

      let canvasFraction = { x: 0.5, y: 0.5 };
      const carCanvas = findCarImageCanvas(el);
      if (carCanvas) {
        // clickX/clickY are overflow-relative when the overflow element exists (unchanged
        // behavior), pane-relative otherwise — mirror markAtPointer's base.
        const baseRect = overflowEl ? overflowEl.getBoundingClientRect() : paneRect;
        const canvasBox = carCanvas.getBoundingClientRect();
        if (canvasBox.width && canvasBox.height) {
          canvasFraction = {
            x: (baseRect.left + clickX - canvasBox.left) / canvasBox.width,
            y: (baseRect.top + clickY - canvasBox.top) / canvasBox.height,
          };
        }
      }

      const frameInfo = estimateFrame();

      const positionPct = {
        x: Math.round((clickX / size.w) * 1000) / 10,
        y: Math.round((clickY / size.h) * 1000) / 10,
      };
      const spin = getFirstSpin();
      const frameImageUrl = spin?.images?.urls?.[frameInfo.frameIndex ?? 0] || '';
      // cxNorm === canvasFraction.x (both are the click's x across the car canvas), so the
      // side resolves synchronously — no need to wait on the image capture.
      const resolvedSide = resolveSideFromGeometry(frameInfo.angleDeg, canvasFraction.x);

      const id = generatePinId();
      const pin: DamagePin = {
        id,
        x: clickX,
        y: clickY,
        canvasFraction,
        stockNumber,
        timestamp: Date.now(),
        thumbnail: '',
        zoomThumbnail: '',
        locationData: {
          positionPct,
          containerSize: size,
          frameIndex: frameInfo.frameIndex,
          viewDescription: frameInfo.viewDescription,
          angleDeg: frameInfo.angleDeg,
          frameImageUrl,
          panelName: '',
          panelId: '',
          resolvedSide,
          confidence: '',
          reasoning: '',
          panelLoading: false,
          aiTimeMs: null,
        },
      };

      // Show the dot INSTANTLY — place the pin now, then capture the frame + zoom crop off
      // the critical path. The capture (big drawImage + JPEG encode) costs ~100ms of main
      // thread, so it's deferred past the confirm step's camera animation — running it at
      // tap time visibly hitched the move. The images are only needed much later, by the
      // deferred LLM call + Document Manager upload on Confirm. The fraction anchor (not
      // raw px) keeps the crosshair exact regardless of the transform state when it runs.
      setPins((prev) => [...prev, pin]);
      const timer: ReturnType<typeof setTimeout> = setTimeout(() => {
        captureTimersRef.current.delete(timer);
        void runCapture(id, canvasFraction);
      }, CAPTURE_DELAY_MS);
      captureTimersRef.current.add(timer);

      return id;
    },
    [containerRef, runCapture]
  );

  const clearPins = useCallback(() => {
    captureTimersRef.current.forEach((t) => clearTimeout(t));
    captureTimersRef.current.clear();
    captureRef.current.clear();
    setPins([]);
  }, []);

  // Cancel any pending deferred captures on unmount.
  useEffect(
    () => () => {
      captureTimersRef.current.forEach((t) => clearTimeout(t));
      captureTimersRef.current.clear();
    },
    []
  );

  return { pins, canvasRect, addPin, clearPins, captureNow };
}
